import { ApolloQueryResult, FetchResult } from '@apollo/client';
import { isGraphQLErrorUnauthorized, isGraphQLErrorReauthRequired } from '@opendoor/auth-fe';
import type { Observability } from 'shared/helpers/observability';
import { GraphQLError } from 'graphql';
import { showToast } from '@opendoor/bricks/complex/Toast';

// Each error should represent a unique way to present the error to the user.

/** Tell the user they are not authorized. */
export const HelloSignUnauthorizedError = Symbol.for('unauthorized-error');
/** Show the user the reauth modal. */
export const HelloSignReauthRequiredError = Symbol.for('reauth-required-error');
/** Tell the user to retry in a few minutes. */
export const HelloSignRetryMinutesError = Symbol.for('retry-minutes');
/** Refresh the page for the user or tell the user to refresh manually. */
export const HelloSignRefreshPageError = Symbol.for('refresh-page');
/** Tell the user to contact us. */
export const HelloSignUnexpectedError = Symbol.for('contact-us');

export type HelloSignError =
  | typeof HelloSignUnauthorizedError
  | typeof HelloSignReauthRequiredError
  | typeof HelloSignRetryMinutesError
  | typeof HelloSignRefreshPageError
  | typeof HelloSignUnexpectedError;

/** Returns the error type if any */
export const getGraphqlErrorType = (
  result: ApolloQueryResult<unknown> | FetchResult,
  observability: Observability,
  analyticsId: string,
): HelloSignError | undefined => {
  if (result.errors && result.errors.length) {
    if (result.errors.some(isGraphQLErrorReauthRequired)) {
      observability.trackEvent('view', `${analyticsId}_reauth-required`);
      return HelloSignReauthRequiredError;
    } else if (result.errors.some(isGraphQLErrorUnauthorized)) {
      observability.trackEvent('view', `${analyticsId}_authorized`);
      return HelloSignUnauthorizedError;
    } else if (result.errors.some(isGraphQLErrorNotFound)) {
      observability.trackEvent('view', `${analyticsId}_not-found`);

      // When we receive NotFound, there's usually stale data showing on the page
      // which can usually be resolved by refreshing the page.
      return HelloSignRefreshPageError;
    } else if (result.errors.some(isGraphQLErrorFailedPrecondition)) {
      observability.trackEvent('view', `${analyticsId}_failed-precondition`);

      // When we receive FailedPrecondition, we can usually retry in a few minutes
      // and the problem will have resolved itself.
      return HelloSignRetryMinutesError;
    }

    let error = undefined;
    let errorMessage = 'error';
    if ('error' in result) {
      error = result.error;
      if (error) {
        errorMessage = error.message.slice(0, 20);
      }
    }
    observability.trackError(`${analyticsId}-${errorMessage}`, error);
    return HelloSignUnexpectedError;
  }
};

/**
 * Shows a toast for the given HelloSign error.
 *
 * Does nothing for `HelloSignReauthRequiredError` which should show the reauth modal instead.
 */
export const showToastForHelloSignError = (error: HelloSignError): void => {
  let message = null;
  switch (error) {
    case HelloSignUnauthorizedError:
      message = 'Sorry, you don’t have permission to view this document.';
      break;
    case HelloSignRefreshPageError:
      message = 'Oops, something went wrong. Please try refreshing the page.';
      break;
    case HelloSignRetryMinutesError:
      message =
        'Oops, something went wrong. Please try again in a few minutes or contact your experience partner.';
      break;
    case HelloSignUnexpectedError:
      message =
        'Oops, something went wrong. Please try again in a few hours or contact your experience partner.';
      break;
  }

  if (message) {
    showToast('error', message, 'cake-hellosign-error-toast', {
      toastOptions: {
        position: 'bottom-left',
      },
    });
  }
};

/** Returns true if a GraphQL error is NotFound (404). */
export const isGraphQLErrorNotFound = (error: GraphQLError): boolean => {
  // TODO: Update Athena error serialization so we don't need to look at the stacktrace.
  // https://opendoor.atlassian.net/browse/SXP-587
  return error.message.startsWith('HTTP Error 404');
};

/** Returns true if a GraphQL error is PreconditionFailed (422). */
export const isGraphQLErrorFailedPrecondition = (error: GraphQLError): boolean => {
  // TODO: Update Athena error serialization so we don't need to look at the stacktrace.
  // https://opendoor.atlassian.net/browse/SXP-587
  return error.message.startsWith('HTTP Error 422');
};
